import { forwardRef, useEffect } from 'react';
import JsBarcode from 'jsbarcode';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { useBoundStore } from 'app/store';
import ZoomInIcon from 'styles/images/iconZoomIn.svg';
import ParkingIcon from 'styles/images/parking-icon.svg';
import ReceiptIcon from 'styles/images/receipt-icon.svg';

import { collectBarcodeData } from '../lib';
import { useReceipt } from '../model';

export const ReceiptView = forwardRef(
  ({ onDownload, onParkingBarcode, receiptData, transformRef, receiptTemplate }, ref) => {
    const { serviceParams } = useBoundStore((state) => state['medicalReceipt']);
    const { hspId } = serviceParams;

    const { isZoomed, setIsZoomed, showZoomIcon, bindDataToElements, zoomHandlers, scale } = useReceipt(
      receiptData,
      hspId,
      ref,
    );

    useEffect(() => {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isWebView = !/Safari/i.test(navigator.userAgent) && /AppleWebKit/i.test(navigator.userAgent);

      if (isIOS && isWebView) {
        document.body.classList.add('ios-webview');
      }

      if (!receiptData || !ref.current || !receiptTemplate) return;

      const timer = setTimeout(() => {
        bindDataToElements();

        const createBarcode = () => {
          const barcodeDataArray = collectBarcodeData(receiptData);

          if (barcodeDataArray.length === 0) return;

          const maxBarcodes = Math.min(barcodeDataArray.length, 4);

          for (let i = 0; i < maxBarcodes; i++) {
            const barcodeData = barcodeDataArray[i];
            if (!barcodeData) continue;

            const barcodeElement = ref.current.querySelector(`#receipt-barcode-${i}`);

            if (barcodeElement) {
              try {
                barcodeElement.style.display = 'block';
                JsBarcode(barcodeElement, barcodeData, {
                  format: 'CODE128',
                  margin: 0,
                  displayValue: false,
                  height: 40,
                  width: 1.2,
                });
              } catch (error) {
                console.error(`바코드 ${i} 생성 실패:`, error);
                barcodeElement.style.display = 'none';
              }
            }
          }

          for (let i = maxBarcodes; i < 4; i++) {
            const barcodeElement = ref.current.querySelector(`#receipt-barcode-${i}`);
            if (barcodeElement) {
              barcodeElement.style.display = 'none';
            }
          }
        };

        requestAnimationFrame(() => {
          setTimeout(() => {
            if (receiptData) createBarcode();
          }, 200);
        });
      }, 100);

      return () => clearTimeout(timer);
    }, [receiptData, ref, receiptTemplate, bindDataToElements]);

    const redirectToPayComplete = () => {
      const payCompleteUrl = serviceParams.payComplete;
      if (payCompleteUrl) {
        window.location.href = payCompleteUrl;
      }
    };

    useEffect(() => {
      if (setIsZoomed) {
        setIsZoomed(isZoomed);
      }
    }, [isZoomed, setIsZoomed]);

    return (
      <>
        <style>
          {`
          .pay-receipt-container {
            width: 100%;
            // height: 100%;
            position: relative;
          }
          .html-wrap {
            width: 100%;
            height: 100%;
            position: relative;
            overflow: hidden;
          }
          .receipt-content {
            width: 100%;
            height: 100%;
            touch-action: none;
            transform: scaleX(0.8) scaleY(0.8);
          }
          .react-transform-wrapper {
            width: 100% !important;
            height: 100% !important;
          }
          .react-transform-component {
            width: 100% !important;
            height: 100% !important;
          }
          .guide-box {
            transition: opacity 0.3s ease-in-out;
            opacity: ${scale <= 1.0 ? 1 : 0};
          }
          .zoom-icon {
            transition: opacity 0.5s ease-in-out;
            opacity: ${showZoomIcon ? 1 : 0};
            pointer-events: none;
          }
          [name="card_company"] {
            white-space: pre;
          }
        `}
        </style>
        <div className='content-article' role='article'>
          <div className='inner-content pay-receipt'>
            <div className='title-wrap'>
              <h3>진료비 영수증</h3>
              <p>두 손가락으로 영수증을 확대/축소할 수 있어요.</p>
            </div>

            <div className='btn-wrap'>
              <button onClick={onDownload}>
                <img src={ReceiptIcon} alt='영수증 아이콘' />
                <span>영수증 다운로드</span>
              </button>
              <button onClick={onParkingBarcode}>
                <img src={ParkingIcon} alt='주차 아이콘' />
                <span>주차 바코드</span>
              </button>
            </div>

            <div className={`pay-receipt-container`}>
              <div className='html-wrap'>
                <TransformWrapper
                  ref={transformRef}
                  minScale={1}
                  maxScale={6}
                  centerOnInit={true}
                  wheel={{ disabled: true }}
                  panning={{ disabled: false }}
                  doubleClick={{ disabled: false }}
                  onZoom={zoomHandlers.onZoom}
                  onTransformed={zoomHandlers.onTransformed}
                >
                  <TransformComponent>
                    <div className='receipt-content' dangerouslySetInnerHTML={{ __html: receiptTemplate }} ref={ref} />
                  </TransformComponent>
                </TransformWrapper>
                <div className='zoom-icon'>
                  <div>
                    <img src={ZoomInIcon} alt='확대하기 아이콘' />
                  </div>
                </div>
              </div>

              <div className='guide-box'>
                <div>
                  <span>
                    본 영수증은 병원에서 발급한 진료비 영수증으로 보험청구 등 공식 증빙자료로 사용할 수 있어요.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className='btm-content'>
            <button type='button' className='btn_n btn_primary' onClick={redirectToPayComplete}>
              돌아가기
            </button>
          </div>
        </div>
      </>
    );
  },
);

ReceiptView.displayName = 'ReceiptView';
